import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { EventModel, Outcome } from './model/types';
import { toChecksumId } from '../utils/ids';

export const ALL_OUTCOMES = 'all';

export function parseIds(param: string | null): string[] {
    if (!param) return [];
    return [...new Set(param.split(',').filter(Boolean))];
}

export interface EventSelection {
    selectedIds: string[];
    selectedOutcomes: Outcome[];
    single: Outcome | null;
    isSingleOutcomeEvent: boolean;
    toggleOutcome: (id: string) => void;
    selectAllOutcomes: () => void;
    clearOutcomes: () => void;
    wallets: string[];
    toggleWallet: (address: string) => void;
    clearWallets: () => void;
}

export function useEventSelection(event: EventModel | null): EventSelection {
    const [searchParams, setSearchParams] = useSearchParams();
    const marketsParam =
        searchParams.get('markets') ?? searchParams.get('market');
    const tradersParam = searchParams.get('traders');

    const isSingleOutcomeEvent = event?.outcomes.length === 1;

    const selectedIds = useMemo(() => {
        if (!event) return [];
        if (event.outcomes.length === 1) return [event.outcomes[0].id];
        if (marketsParam === ALL_OUTCOMES) {
            return event.outcomes.map(o => o.id);
        }
        const known = new Set(event.outcomes.map(o => o.id));
        return parseIds(marketsParam).filter(id => known.has(id));
    }, [event, marketsParam]);

    const selectedOutcomes = useMemo(
        () =>
            event
                ? selectedIds
                      .map(id => event.outcomes.find(o => o.id === id))
                      .filter((o): o is Outcome => o !== undefined)
                : [],
        [event, selectedIds]
    );

    const single = selectedOutcomes.length === 1 ? selectedOutcomes[0] : null;

    const wallets = useMemo(
        () => [...new Set(parseIds(tradersParam).map(toChecksumId))],
        [tradersParam]
    );

    const setParam = useCallback(
        (name: string, values: readonly string[], drop: readonly string[]) => {
            setSearchParams(
                prev => {
                    const params = new URLSearchParams(prev);
                    for (const key of drop) params.delete(key);
                    if (values.length > 0) params.set(name, values.join(','));
                    else params.delete(name);
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const toggleOutcome = useCallback(
        (id: string) => {
            if (isSingleOutcomeEvent) return;
            const next = selectedIds.includes(id)
                ? selectedIds.filter(x => x !== id)
                : [...selectedIds, id];
            setParam('markets', next, ['market', 'traders']);
        },
        [isSingleOutcomeEvent, selectedIds, setParam]
    );

    const selectAllOutcomes = useCallback(() => {
        if (isSingleOutcomeEvent) return;
        setParam('markets', [ALL_OUTCOMES], ['market', 'traders']);
    }, [isSingleOutcomeEvent, setParam]);

    const clearOutcomes = useCallback(
        () => setParam('markets', [], ['market', 'traders']),
        [setParam]
    );

    const toggleWallet = useCallback(
        (address: string) => {
            const id = toChecksumId(address);
            const next = wallets.includes(id)
                ? wallets.filter(w => w !== id)
                : [...wallets, id];
            setParam('traders', next, []);
        },
        [wallets, setParam]
    );

    const clearWallets = useCallback(
        () => setParam('traders', [], []),
        [setParam]
    );

    return {
        selectedIds,
        selectedOutcomes,
        single,
        isSingleOutcomeEvent: isSingleOutcomeEvent === true,
        toggleOutcome,
        selectAllOutcomes,
        clearOutcomes,
        wallets,
        toggleWallet,
        clearWallets,
    };
}
